// ./src/store/slices/contactSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';

export interface Relative {
  relativeName: string;
  relativeCategory: string;
  relativeRsvpStatus?: string;
}

// Extended relative type for guest editing - tracks invite status
export interface RelativeWithInviteStatus extends Relative {
  isInvited: boolean;  // Whether this relative is invited to this event
}

// Define the contact data structure
export interface Contact {
  authorId: number;
  contactId: number;
  firstName: string;
  lastName: string;
  email: string | null; // NULLABLE: Can be null if not provided
  phone: string | null; // NULLABLE: Can be null if not provided
  relatives: Relative[];
  tags: string[];
  savedToDb: boolean;
}

// Initial state for the contacts slice
interface ContactState {
  contacts: Contact[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

interface UpdateContactTagsPayload {
  contactId: number; // ID of the contact to update
  tags: string[]; // New tags to update
}
interface UpdateContactRelativesPayload {
  contactId: number;
  relatives: Relative[];
}
interface UpdateContactFieldPayload {
  contactId: number;
  field: keyof Contact;
  value: string;
}

// Initial state for the slice
const initialState: ContactState = {
  contacts: [],
  status: 'idle',
  error: null,
};

// Slice for managing contact state
// Slice for managing contact state
const contactSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    // Action to manually set contacts (if necessary)
    setContacts(state, action: PayloadAction<Contact[]>) {
      state.contacts = action.payload;
      state.status = 'succeeded';
      state.error = null;
    },
    // Action to add a new contact
    addContact(state, action: PayloadAction<Contact>) {
      state.contacts.push(action.payload);
      state.status = 'succeeded';
      state.error = null;
    },
    // Action to reset contacts state
    resetContactsState(state) {
      state.contacts = [];
      state.status = 'idle';
      state.error = null;
    },
    // Action to handle loading state
    setContactsLoading(state) {
      state.status = 'loading';
    },
    // Action to handle error state
    setContactsError(state, action: PayloadAction<string>) {
      state.status = 'failed';
      state.error = action.payload;
    },
    updateContactField(state, action: PayloadAction<UpdateContactFieldPayload>) {
      const { contactId, field, value } = action.payload;
      const contact = state.contacts.find((c) => c.contactId === contactId);
      if (contact) {
        (contact[field] as string) = value;
      }
    },
    updateContactTags(state, action: PayloadAction<UpdateContactTagsPayload>) {
      const { contactId, tags } = action.payload;
      const contact = state.contacts.find((c) => c.contactId === contactId);
      if (contact) {
        contact.tags = tags; // Update tags for the contact
      }
    },
    updateContactRelatives(state, action: PayloadAction<UpdateContactRelativesPayload>) {
      const { contactId, relatives } = action.payload;
      const contact = state.contacts.find((c) => c.contactId === contactId);
      if (contact) {
        contact.relatives = relatives;
      }
    },
    updateContact(state, action: PayloadAction<{ tempId: number; updatedContact: Contact }>) {
      const { tempId, updatedContact } = action.payload;

      // Find the contact index by tempId
      const contactIndex = state.contacts.findIndex((contact) => contact.contactId === tempId);
      if (contactIndex === -1) {
        console.error('Contact not found with tempId:', tempId);
        return; // Exit if no matching contact is found
      }

      // Replace the temporary contact with the updated contact
      state.contacts[contactIndex] = updatedContact;
    },
    // Action to remove a contact by contactId
    removeContact(state, action: PayloadAction<number>) {
      const contactId = action.payload;
      state.contacts = state.contacts.filter((contact) => contact.contactId !== contactId);
      state.status = 'succeeded';
    },
  },
});

// Export the actions
export const { setContacts, addContact, resetContactsState, setContactsLoading, setContactsError, updateContactTags, updateContactRelatives, updateContactField, updateContact, removeContact } = contactSlice.actions;

export default contactSlice.reducer;
